import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  HOUR_STEPS,
  MINUTE_STEPS,
  MONTH_DAY_MAX,
  PRESET_KINDS,
  describeCron,
  type SchedulePresetKind,
} from "@prodgy/schedule";

import { cn } from "@/infrastructure/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import {
  draftCron,
  draftProblem,
  withCustomCron,
  type DraftProblem,
  type FrequencyDraft,
} from "../utils/frequencyDraft";
import { formatRun, nextRuns, timezoneOptions } from "../utils/schedule";

interface FrequencyPickerProps {
  draft: FrequencyDraft;
  onDraftChange: (draft: FrequencyDraft) => void;
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  /** Erro devolvido pela api no fuso horário. */
  timezoneError?: string;
}

// Segunda primeiro, como no calendário de quem usa o Prodgy; domingo é 0 no cron.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const HOURS = Array.from({ length: 24 }, (_, h) => h);

/**
 * Frequência de um Job por opções guiadas (AB#87330): intervalos com janela e
 * dias úteis, diário, dias úteis, semanal, mensal — e o cron como opção
 * avançada, por último. Embaixo, a frase do agendamento e as próximas
 * execuções no fuso escolhido, antes de salvar.
 */
export function FrequencyPicker({
  draft,
  onDraftChange,
  timezone,
  onTimezoneChange,
  timezoneError,
}: FrequencyPickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const scheduleLocale = locale.startsWith("pt") ? "pt" : "en";

  const set = (patch: Partial<FrequencyDraft>) => onDraftChange({ ...draft, ...patch });
  const problem = draftProblem(draft);
  const cron = draftCron(draft);
  const runs = useMemo(() => (problem ? [] : nextRuns(cron, timezone)), [problem, cron, timezone]);

  const timeField = (
    <div className="space-y-1.5">
      <Label htmlFor="job-time">{t("jobs.form.time")}</Label>
      <Input id="job-time" type="time" value={draft.time} onChange={(e) => set({ time: e.target.value })} />
    </div>
  );

  const interval = draft.kind === "everyMinutes" || draft.kind === "everyHours";

  return (
    <div className="grid gap-3 rounded-md border border-border/60 p-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label>{t("jobs.form.frequency")}</Label>
        <Select value={draft.kind} onValueChange={(value) => set({ kind: value as SchedulePresetKind })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESET_KINDS.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {t(`jobs.form.presets.${kind}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {draft.kind === "everyMinutes" && (
        <div className="space-y-1.5">
          <Label>{t("jobs.form.every")}</Label>
          <Select value={String(draft.minutes)} onValueChange={(value) => set({ minutes: Number(value) as FrequencyDraft["minutes"] })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MINUTE_STEPS.map((step) => (
                <SelectItem key={step} value={String(step)}>
                  {t("jobs.form.minutes", { count: step })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {draft.kind === "everyHours" && (
        <div className="space-y-1.5">
          <Label>{t("jobs.form.every")}</Label>
          <Select value={String(draft.hours)} onValueChange={(value) => set({ hours: Number(value) as FrequencyDraft["hours"] })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOUR_STEPS.map((step) => (
                <SelectItem key={step} value={String(step)}>
                  {t("jobs.form.hours", { count: step })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {(draft.kind === "daily" || draft.kind === "weekdays") && timeField}

      {draft.kind === "weekly" && (
        <>
          {timeField}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("jobs.form.days")}</Label>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("jobs.form.days")}>
              {WEEK_ORDER.map((day) => {
                const selected = draft.days.includes(day);
                return (
                  <Button
                    key={day}
                    type="button"
                    size="sm"
                    variant={selected ? "default" : "outline"}
                    aria-pressed={selected}
                    className="min-w-12"
                    onClick={() =>
                      set({
                        days: selected
                          ? draft.days.filter((d) => d !== day)
                          : [...draft.days, day].sort((a, b) => a - b),
                      })
                    }
                  >
                    {weekdayName(day, locale)}
                  </Button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {draft.kind === "monthly" && (
        <>
          <div className="space-y-1.5">
            <Label>{t("jobs.form.monthDay")}</Label>
            <Select
              value={String(draft.monthDay)}
              onValueChange={(value) => set({ monthDay: value === "last" ? "last" : Number(value) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: MONTH_DAY_MAX }, (_, i) => i + 1).map((day) => (
                  <SelectItem key={day} value={String(day)}>
                    {day}
                  </SelectItem>
                ))}
                <SelectItem value="last">{t("jobs.form.lastDay")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {timeField}
        </>
      )}

      {interval && (
        <div className="space-y-3 sm:col-span-2">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={draft.windowOn} onCheckedChange={(checked) => set({ windowOn: checked === true })} />
              {t("jobs.form.window")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.weekdaysOnly}
                onCheckedChange={(checked) => set({ weekdaysOnly: checked === true })}
              />
              {t("jobs.form.weekdaysOnly")}
            </label>
          </div>
          {draft.windowOn && (
            <div className="grid grid-cols-2 gap-3">
              <HourSelect
                label={t("jobs.form.windowFrom")}
                value={draft.window.from}
                hours={HOURS}
                onChange={(from) => set({ window: { ...draft.window, from } })}
              />
              <HourSelect
                label={t("jobs.form.windowTo")}
                value={draft.window.to}
                hours={HOURS.map((h) => h + 1)}
                onChange={(to) => set({ window: { ...draft.window, to } })}
              />
            </div>
          )}
        </div>
      )}

      {draft.kind === "custom" && (
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="job-cron">{t("jobs.form.cron")}</Label>
          <Input
            id="job-cron"
            className="font-mono"
            value={draft.cron}
            aria-invalid={problem !== null}
            // A troca para o preset equivalente espera a pessoa sair do campo:
            // no meio de "0 9 * * 1-5" passa "0 9 * * 1", que já é um preset.
            onChange={(e) => set({ cron: e.target.value })}
            onBlur={() => onDraftChange(withCustomCron(draft, draft.cron))}
          />
          <p className="text-xs text-muted-foreground">{t("jobs.form.cronHelp")}</p>
        </div>
      )}

      {problem && (
        <p className="text-xs text-destructive sm:col-span-2" role="alert">
          {problemMessage(problem, t)}
        </p>
      )}

      <div className="space-y-1.5 sm:col-span-2">
        <Label>{t("jobs.form.timezone")}</Label>
        <TimezoneSelect value={timezone} onChange={onTimezoneChange} />
        {timezoneError && <p className="text-xs text-destructive">{timezoneError}</p>}
      </div>

      <div className="rounded-md bg-muted/50 p-3 text-sm sm:col-span-2" aria-live="polite">
        {problem ? (
          <p className="text-muted-foreground">{t("jobs.form.noPreview")}</p>
        ) : (
          <>
            <p className="font-medium">{describeCron(cron, { locale: scheduleLocale, timezone })}</p>
            {runs.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-muted-foreground">{t("jobs.form.nextRuns")}</p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {runs.map((run) => (
                    <li key={run.toISOString()}>{formatRun(run, timezone, locale)}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function problemMessage(problem: DraftProblem, t: Translate): string {
  if (problem.key === "invalidCronField") {
    return t("jobs.form.invalidCronField", { field: t(`jobs.form.cronFields.${problem.field}`) });
  }
  return t(`jobs.form.${problem.key}`);
}

function HourSelect({
  label,
  value,
  hours,
  onChange,
}: {
  label: string;
  value: number;
  hours: number[];
  onChange: (hour: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={String(value)} onValueChange={(next) => onChange(Number(next))}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {hours.map((hour) => (
            <SelectItem key={hour} value={String(hour)}>
              {`${String(hour).padStart(2, "0")}:00`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Fuso por busca: a lista do navegador tem centenas de nomes. */
function TimezoneSelect({ value, onChange }: { value: string; onChange: (timezone: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const options = useMemo(() => timezoneOptions(value), [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          {value}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("jobs.form.timezoneSearch")} />
          <CommandList>
            <CommandEmpty>{t("jobs.form.timezoneEmpty")}</CommandEmpty>
            <CommandGroup>
              {options.map((zone) => (
                <CommandItem
                  key={zone}
                  value={zone}
                  onSelect={() => {
                    onChange(zone);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", zone === value ? "opacity-100" : "opacity-0")} />
                  {zone}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Nome curto do dia da semana no idioma da pessoa (0 = domingo). */
function weekdayName(day: number, locale: string): string {
  // 2026-10-18 é um domingo: soma o dia para cair no dia da semana pedido.
  const date = new Date(Date.UTC(2026, 9, 18 + day, 12));
  return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(date);
}
